/**
 * The made-to-measure fabrics, searched, filtered and sorted by the API and
 * loaded page by page, with the CSV import / export. Admin only.
 */
import { useEffect, useState } from 'react';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowUp,
  faCheck,
  faCircleNotch,
  faDownload,
  faPencilAlt,
  faSearch,
  faTrash,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';
import {
  DMS_PRODUCTLINES,
  getDmsProductLineLabel,
  MTM_ROUTE_PATH,
  PLATFORM,
  ROLES,
} from '@/shared/constants';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { snackbar } from '@/shared/ui/snackbar';
import { useDmsCsv } from '../use-dms-csv';
import { useDeleteItem } from '../use-referential';
import plusIcon from '@/assets/images/plus.svg';
import { deleteDmsItem, lastLogQueryOptions } from '../api';
import { fabricPagesQueryOptions } from './api';
import { fabricBooksQueryOptions } from '../fabric-books/api';
import {
  csvExportFileName,
  csvRowsToFabrics,
  FABRIC_COLUMNS,
  fabricDataSchema,
  formatCsvDate,
  nextFabricSort,
  type DmsFabric,
} from './fabric-form';
import { FabricDialog } from './FabricDialog';
import { ariaSort } from '@/shared/ui/aria-sort';
import { Select } from '@/shared/ui/Select';
import { kebabCase } from '@/shared/lib/kebab-case';

export function FabricsPage() {
  const user = getUserProfile();
  const isAdmin = user?.role === ROLES.ADMIN;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user && !isAdmin) hardNavigate(`/${MTM_ROUTE_PATH}/main`);
  }, [user, isAdmin]);

  const [productLine, setProductLine] = useState<string | null>(null);
  const [fabricBook, setFabricBook] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sort, setSort] = useState<{ by: string | null; ascending: boolean }>({
    by: 'data.order',
    ascending: true,
  });
  const [refresh, setRefresh] = useState(0);
  const [dialog, setDialog] = useState<{ item: DmsFabric | null } | null>(null);
  const [removing, setRemoving] = useState<DmsFabric | null>(null);

  const fabrics = useInfiniteQuery({
    ...fabricPagesQueryOptions(
      {
        search: appliedSearch,
        sortBy: sort.by,
        sortOrder: sort.ascending ? 'asc' : 'desc',
        productLine,
        fabricBook,
        refresh,
      },
      fabricDataSchema,
    ),
    enabled: isAdmin,
  });
  useEffect(() => {
    if (fabrics.isError)
      snackbar(
        'Unexpected error while loading the items, please try again later',
      );
  }, [fabrics.isError]);
  const books = useQuery({ ...fabricBooksQueryOptions(), enabled: isAdmin });
  const lastUpload = useQuery({
    ...lastLogQueryOptions('dmsFabricCsvUpload', refresh),
    enabled: isAdmin,
  });
  useEffect(() => {
    if (lastUpload.isError)
      snackbar('Failed to get the last update date of fabrics CSV');
  }, [lastUpload.isError]);

  const items = fabrics.data?.pages.flatMap((page) => page.data) ?? [];
  const nextCursor = fabrics.data?.pages.at(-1)?.nextCursor ?? null;
  const reload = () => setRefresh((count) => count + 1);
  const onCsvUploaded = () => {
    reload();
    void queryClient.invalidateQueries({ queryKey: ['dms-items'] });
  };

  const remove = useDeleteItem(
    (item: DmsFabric) => deleteDmsItem(item.id),
    reload,
  );

  const { csvRows, csvInput, uploadCsv, downloadCsv, onCsvChosen } = useDmsCsv({
    type: 'fabric',
    label: 'fabrics',
    logType: 'dmsFabricCsvUpload',
    toItems: csvRowsToFabrics,
    exportFileName: csvExportFileName,
    onUploaded: onCsvUploaded,
  });

  const isLoading =
    fabrics.isPending || fabrics.isFetchingNextPage || uploadCsv.isPending;

  const closeDialog = (result: 'Confirm' | 'Cancel') => {
    setDialog(null);
    if (result === 'Confirm') reload();
  };

  if (!isAdmin) return null;

  const filterSelect = (
    label: string,
    value: string | null,
    options: { label: string; value: string }[],
    onChange: (value: string | null) => void,
    testId: string,
  ) => (
    <div className="field field-selector" style={{ width: 150 }}>
      <Select
        aria-label={label}
        testId={testId}
        placeholder={`--${label}--`}
        options={options}
        value={value ?? ''}
        onChange={(next) => onChange(next || null)}
      />
    </div>
  );

  return (
    <div className="container summary">
      <TopMenu platform={PLATFORM.DMS} />
      <div className="summary-table mb-12 mt-4">
        <div className="relative mb-4 flex items-end justify-between">
          <div>
            <h2>Fabrics</h2>
            <div className="flex items-center">
              <div style={{ marginRight: 20 }}>
                {filterSelect(
                  'Filter by product line',
                  productLine,
                  DMS_PRODUCTLINES,
                  setProductLine,
                  'mtm-fabric-filter-product-line',
                )}
              </div>
              {filterSelect(
                'Filter by fabric book',
                fabricBook,
                (books.data ?? []).map((book) => ({
                  label: book.name,
                  value: book.name,
                })),
                setFabricBook,
                'mtm-fabric-filter-fabric-book',
              )}
            </div>
            <form
              className="input-group mt-2"
              style={{ maxWidth: 300 }}
              onSubmit={(event) => {
                event.preventDefault();
                setAppliedSearch(search);
              }}
            >
              <input
                type="text"
                className="form-control shadow-none"
                placeholder="Search"
                aria-label="Search"
                data-testid="mtm-fabric-search-input"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <button
                type="submit"
                className="btn text-muted bg-white px-3 shadow-none"
                style={{ border: '1px solid #ced4da' }}
                aria-label="Search"
                data-testid="mtm-fabric-search-button"
              >
                <FontAwesomeIcon icon={faSearch} />
              </button>
            </form>
            <div className="search-hint">
              To activate your search, please click the magnifying glass icon or
              press Enter.
            </div>
          </div>

          <button
            type="button"
            className="add-btn absolute-x-center flex items-center"
            data-testid="header-add-fabric"
            onClick={() => {
              if (!isLoading) setDialog({ item: null });
            }}
          >
            <img src={plusIcon} alt="" />
            <span className="pl-2">Add a new fabric</span>
          </button>

          <div className="flex flex-col items-end">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faUpload} style={{ fontSize: 24 }} />
              <input
                ref={csvInput}
                className="field field-upload"
                name="csv-upload"
                id="csv-upload"
                aria-label="Fabrics CSV file"
                data-testid="mtm-fabric-upload-input"
                type="file"
                accept=".csv"
                style={{ height: 30 }}
                onChange={(event) => onCsvChosen(event.target.files?.[0])}
              />
              <button
                type="button"
                className="button button-form"
                style={{ height: 30 }}
                data-testid="mtm-fabric-upload-button"
                disabled={uploadCsv.isPending || !csvRows}
                onClick={() => uploadCsv.mutate()}
              >
                Save
              </button>
            </div>
            <div className="mt-1" data-testid="mtm-fabric-upload-last-date">
              Last updated date: {formatCsvDate(lastUpload.data?.createdAt)}
            </div>
            <div className="mt-1" data-testid="mtm-fabric-upload-file-name">
              File name: {lastUpload.data?.filename || '-'}
            </div>
            <div className="flex items-center">
              <button
                type="button"
                className="button button-form"
                data-testid="mtm-fabric-download-button"
                onClick={() => downloadCsv.mutate()}
              >
                Download fabrics in CSV
              </button>
              <FontAwesomeIcon
                icon={faDownload}
                style={{ fontSize: 24, marginLeft: 10 }}
              />
            </div>
          </div>
        </div>
        <div className="mb-6">
          <div className="table-container">
            <div className="items-container" role="region" aria-label="Fabrics">
              <table
                className="table items"
                style={{
                  display: 'block',
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                }}
              >
                <thead>
                  <tr>
                    {FABRIC_COLUMNS.map(({ key, label, width, sortBy }) => (
                      <th
                        key={key}
                        style={{ width }}
                        aria-sort={
                          sortBy
                            ? ariaSort(sort.by === sortBy, !sort.ascending)
                            : undefined
                        }
                      >
                        {sortBy ? (
                          <button
                            type="button"
                            className="cursor-pointer"
                            data-testid={`mtm-fabric-header-${kebabCase(key)}`}
                            onClick={() =>
                              setSort((current) =>
                                nextFabricSort(current, sortBy),
                              )
                            }
                          >
                            {label}{' '}
                            {sort.by === sortBy && (
                              <FontAwesomeIcon
                                icon={sort.ascending ? faArrowUp : faArrowDown}
                              />
                            )}
                          </button>
                        ) : (
                          label
                        )}
                      </th>
                    ))}
                    <th style={{ width: '5%' }}>Picture</th>
                    <th style={{ width: '5%' }}>Evening offer</th>
                    <th style={{ width: '6%' }} className="edit-header">
                      actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fabrics.isPending ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center">
                        <FontAwesomeIcon
                          icon={faCircleNotch}
                          spin
                          size="2x"
                          className="text-muted"
                        />
                        <div className="text-muted mt-2">Loading items...</div>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {items.map((fabric) => (
                        <tr key={fabric.id} className="user-row">
                          {FABRIC_COLUMNS.map(({ key, width }) => {
                            const value =
                              key === 'productLine'
                                ? getDmsProductLineLabel(
                                    fabric.data.productLine ?? '',
                                  )
                                : fabric.data[key];
                            const text = value == null ? '' : String(value);
                            return (
                              <td
                                key={key}
                                style={{ width }}
                                title={text}
                                data-testid={`mtm-fabric-row-${kebabCase(key)}`}
                              >
                                {text}
                              </td>
                            );
                          })}
                          <td
                            style={{ width: '5%' }}
                            data-testid="mtm-fabric-row-picture"
                          >
                            {fabric.data.picture && (
                              <FontAwesomeIcon
                                icon={faCheck}
                                aria-label="Picture set"
                              />
                            )}
                          </td>
                          <td
                            style={{ width: '5%' }}
                            data-testid="mtm-fabric-row-evening-offer"
                          >
                            {fabric.data.eveningOffer && (
                              <FontAwesomeIcon
                                icon={faCheck}
                                aria-label="Evening offer"
                              />
                            )}
                          </td>
                          <td style={{ width: '6%' }} className="edit-action">
                            <button
                              type="button"
                              className="action-icon"
                              aria-label={`Edit ${fabric.data.code ?? ''}`}
                              data-testid="mtm-fabric-row-edit-button"
                              onClick={() => {
                                if (!isLoading) setDialog({ item: fabric });
                              }}
                            >
                              <FontAwesomeIcon icon={faPencilAlt} />
                            </button>
                            <button
                              type="button"
                              className="action-icon ml-2"
                              aria-label={`Remove ${fabric.data.code ?? ''}`}
                              data-testid="mtm-fabric-row-delete-button"
                              onClick={() => {
                                if (!isLoading) setRemoving(fabric);
                              }}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={18} className="py-4! text-left!">
                          {fabrics.isFetchingNextPage && (
                            <div className="text-muted">
                              <FontAwesomeIcon icon={faCircleNotch} spin />{' '}
                              Loading more items...
                            </div>
                          )}
                          {nextCursor ? (
                            <button
                              type="button"
                              className="btn btn-link shadow-none"
                              data-testid="mtm-fabric-load-more-button"
                              onClick={() => void fabrics.fetchNextPage()}
                            >
                              Load more items...
                            </button>
                          ) : (
                            <span className="text-muted">No more items</span>
                          )}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {isLoading && (
        <FontAwesomeIcon
          icon={faCircleNotch}
          spin
          className="loading-icon"
          aria-label="Loading"
        />
      )}
      {dialog && (
        <FabricDialog
          key={dialog.item?.id ?? 'new'}
          item={dialog.item}
          productLine={productLine}
          onClose={closeDialog}
        />
      )}
      {removing !== null && (
        <ConfirmDialog
          title="Are you sure?"
          text="This action cannot be undone. This item will be deleted immediately."
          onConfirm={() => {
            if (removing) remove.mutate(removing);
            setRemoving(null);
          }}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
}
